import { isValidTemplateLanguage } from './template-languages';
import type { CarouselCard, CreateTemplateInput, TemplateButton } from './schema';

/**
 * Pre-flight validator for CreateTemplateInput. Catches every rule Meta
 * enforces server-side before the request crosses the wire so vendors get
 * an actionable error from the form rather than a generic
 * `(#100) Invalid parameter` round-trip.
 *
 * Port of LeadSync.WhatsApp.Templates.TemplateValidator. Source rules:
 * developers.facebook.com/docs/whatsapp/business-management-api/message-templates/
 *
 * Returns null when valid, otherwise a vendor-readable explanation of the
 * first failure. Caller surfaces directly via toast / inline error.
 */

const NAME_REGEX = /^[a-z0-9_]+$/;
const POSITIONAL_VAR = /\{\{(\d+)\}\}/g;
const NAMED_VAR = /\{\{([a-z][a-z0-9_]*)\}\}/g;

export function validateTemplate(dto: CreateTemplateInput): string | null {
  if (!dto) return 'Template payload is missing.';

  // Name
  if (!dto.templateName || dto.templateName.trim().length === 0)
    return 'Template name is required.';
  if (!NAME_REGEX.test(dto.templateName))
    return 'Template name must use only lowercase letters, digits, and underscores.';
  if (dto.templateName.length > 512) return 'Template name is too long (max 512 characters).';

  // Language
  if (!isValidTemplateLanguage(dto.language))
    return `Language code '${dto.language}' is not one Meta accepts. Use the canonical code (e.g. en_US, pt_BR, hi).`;

  // Validity period (message_send_ttl_seconds) — category-dependent range.
  const ttlErr = validateTtl(dto);
  if (ttlErr) return ttlErr;

  // Category — per-category divergence
  return dto.category === 'AUTHENTICATION' ? validateAuthentication(dto) : validateStandard(dto);
}

/**
 * Meta's per-category message_send_ttl_seconds ranges; -1 = explicit default.
 *
 * MARKETING is deliberately excluded: Meta only accepts this field on
 * MARKETING templates via the separate Marketing Messages Lite (MM Lite)
 * API — the standard Cloud API endpoint this platform exclusively talks to
 * (`graph.facebook.com`, see shared/meta/client.ts) rejects it outright with
 * a generic `(#100) Invalid parameter`. Blocking it here client-side turns
 * that into an actionable message instead of a confusing round-trip.
 */
function validateTtl(dto: CreateTemplateInput): string | null {
  const ttl = dto.messageSendTtlSeconds;
  if (ttl === undefined || ttl === null) return null;
  if (dto.category === 'MARKETING') {
    return "Custom validity period isn't available for Marketing templates on this platform — Meta only supports it via the separate Marketing Messages Lite API, which isn't integrated here. Leave this blank to use Meta's default (30 days).";
  }
  if (ttl === -1) return null;
  const ranges: Record<string, [number, number]> = {
    AUTHENTICATION: [30, 900],
    UTILITY: [30, 43200],
  };
  const range = ranges[dto.category];
  if (!range) return null;
  const [min, max] = range;
  if (ttl < min || ttl > max)
    return `Validity period for ${dto.category} templates must be between ${min} and ${max} seconds (or -1 for Meta's default).`;
  return null;
}

function validateAuthentication(dto: CreateTemplateInput): string | null {
  // AUTHENTICATION uses a fixed shape — header / body-text / footer-text /
  // regular buttons are forbidden. Only OTP-related fields apply.
  if (dto.header && dto.header.format !== 'NONE')
    return "AUTHENTICATION templates can't have a header.";
  if (dto.body?.text && dto.body.text.trim().length > 0)
    return 'AUTHENTICATION templates have an auto-generated body — leave the body text blank.';
  if (dto.footer?.text)
    return 'AUTHENTICATION templates use Code expiration minutes instead of a footer text.';
  if (dto.buttons && dto.buttons.length > 0)
    return "AUTHENTICATION templates have a built-in OTP button — don't add custom buttons.";

  if (
    dto.codeExpirationMinutes !== undefined &&
    dto.codeExpirationMinutes !== null &&
    (dto.codeExpirationMinutes < 1 || dto.codeExpirationMinutes > 90)
  )
    return 'Code expiration must be between 1 and 90 minutes.';

  const otpType = (dto.otpButtonType ?? 'COPY_CODE').toUpperCase();
  if (otpType !== 'COPY_CODE')
    return 'Only COPY_CODE OTP is supported at v1. ONE_TAP / ZERO_TAP need Android signing-cert hash + zero-tap legal acceptance — coming later.';

  if (!isPlainButtonText(dto.otpButtonText))
    return 'OTP button label must be plain text — no variables, emojis, newlines, or formatting (* _ ~ `).';

  return null;
}

function validateStandard(dto: CreateTemplateInput): string | null {
  // Carousel and LTO are mutually exclusive template shapes.
  if (dto.carousel && dto.limitedTimeOffer)
    return 'A template cannot be both a carousel and a limited-time offer.';

  // Header
  const hf = (dto.header?.format ?? 'NONE').toUpperCase();
  if (!['NONE', 'TEXT', 'IMAGE', 'VIDEO', 'GIF', 'DOCUMENT', 'LOCATION'].includes(hf))
    return `Header format '${hf}' is not supported.`;
  if (hf === 'TEXT') {
    if (!dto.header?.text || dto.header.text.trim().length === 0)
      return 'TEXT header requires header text.';
    if (dto.header.text.length > 60) return 'Header text is too long (max 60 characters).';
    const headerVarCount =
      countMatches(dto.header.text, POSITIONAL_VAR) + countMatches(dto.header.text, NAMED_VAR);
    if (headerVarCount > 1) return 'TEXT header may contain at most one variable.';
  }
  if (
    (hf === 'IMAGE' || hf === 'VIDEO' || hf === 'GIF' || hf === 'DOCUMENT') &&
    !dto.header?.handleId
  )
    return `${hf} header requires a media handle (upload a media file first).`;

  // Body
  if (!dto.body?.text || dto.body.text.trim().length === 0)
    return 'Body text is required for MARKETING / UTILITY templates.';
  // Limited-Time Offer templates have a tighter Meta-documented body limit
  // (600 chars) than the general 1024 cap.
  if (dto.limitedTimeOffer && dto.body.text.length > 600)
    return 'Body text is too long for a limited-time offer template (max 600 characters).';
  if (dto.body.text.length > 1024) return 'Body text is too long (max 1024 characters).';

  const positional = collectMatches(dto.body.text, POSITIONAL_VAR);
  const named = collectMatches(dto.body.text, NAMED_VAR);
  if (positional.size > 0 && named.size > 0)
    return 'Body uses a mix of positional ({{1}}) and named ({{name}}) variables — pick one format.';

  const format = (dto.parameterFormat ?? 'POSITIONAL').toUpperCase();
  if (named.size > 0 && format !== 'NAMED')
    return 'Body uses named variables — set parameter format to NAMED.';
  if (positional.size > 0 && format === 'NAMED')
    return 'Parameter format is NAMED but body uses positional variables ({{1}}, {{2}}, …).';

  const variableCount = Math.max(positional.size, named.size);
  if (variableCount > 0) {
    if (!dto.bodyExamples || dto.bodyExamples.length !== variableCount)
      return `Body has ${variableCount} variable(s) — provide an example value for each.`;
    if (
      format === 'NAMED' &&
      (!dto.bodyVariableNames || dto.bodyVariableNames.length !== variableCount)
    )
      return `Body has ${variableCount} named variable(s) — provide a name for each.`;

    // Variable placement — Meta rejects these server-side with a generic
    // `(#100) Invalid parameter`; catching them here keeps the error usable.
    const bodyTrimmed = dto.body.text.trim();
    const ANY_VAR_AT_START = /^\{\{(?:\d+|[a-z][a-z0-9_]*)\}\}/;
    const ANY_VAR_AT_END = /\{\{(?:\d+|[a-z][a-z0-9_]*)\}\}$/;
    if (ANY_VAR_AT_START.test(bodyTrimmed))
      return 'Body cannot start with a variable — Meta rejects it. Add text before the first variable.';
    if (ANY_VAR_AT_END.test(bodyTrimmed))
      return 'Body cannot end with a variable — Meta rejects it. Add text after the last variable (e.g. a closing sentence).';
    if (/\}\}\s*\{\{/.test(dto.body.text))
      return 'Variables cannot sit next to each other — Meta rejects adjacent parameters. Separate them with text.';
  }

  // Footer
  if (dto.footer?.text) {
    if (dto.footer.text.length > 60) return 'Footer text is too long (max 60 characters).';
    if (POSITIONAL_VAR.test(dto.footer.text) || NAMED_VAR.test(dto.footer.text)) {
      // Reset regex global state because we used .test on a /g regex above.
      POSITIONAL_VAR.lastIndex = 0;
      NAMED_VAR.lastIndex = 0;
      return 'Footer text cannot contain variables.';
    }
    POSITIONAL_VAR.lastIndex = 0;
    NAMED_VAR.lastIndex = 0;
  }

  // Buttons — Meta's current rules: max 10 total; max 2 URL; max 1 phone;
  // max 1 copy-code; quick replies fill the rest but must be grouped
  // contiguously (all adjacent, not interleaved with other types).
  if (dto.buttons && dto.buttons.length > 0) {
    if (dto.buttons.length > 10) return 'A template can have at most 10 buttons.';
    const urlCount = dto.buttons.filter((b) => b.type === 'URL').length;
    if (urlCount > 2) return 'At most 2 URL buttons are allowed.';
    const phoneCount = dto.buttons.filter((b) => b.type === 'PHONE_NUMBER').length;
    if (phoneCount > 1) return 'At most 1 PHONE_NUMBER button is allowed.';
    const copyCodeCount = dto.buttons.filter((b) => b.type === 'COPY_CODE').length;
    if (copyCodeCount > 1) return 'At most 1 COPY_CODE button is allowed.';

    const groupingErr = validateButtonGrouping(dto.buttons);
    if (groupingErr) return groupingErr;

    for (const b of dto.buttons) {
      const err = validateButton(b);
      if (err) return err;
    }
  }

  // Carousel / Limited-Time Offer sub-shapes
  if (dto.carousel) {
    const err = validateCarousel(dto);
    if (err) return err;
  }
  if (dto.limitedTimeOffer) {
    const err = validateLimitedTimeOffer(dto);
    if (err) return err;
  }

  return null;
}

/**
 * Meta requires quick-reply buttons to be grouped: all QUICK_REPLYs adjacent
 * (leading or trailing), never interleaved with other button types.
 */
function validateButtonGrouping(buttons: TemplateButton[]): string | null {
  const first = buttons.findIndex((b) => b.type === 'QUICK_REPLY');
  if (first === -1) return null;
  let last = first;
  for (let i = buttons.length - 1; i >= 0; i--) {
    if (buttons[i]!.type === 'QUICK_REPLY') {
      last = i;
      break;
    }
  }
  for (let i = first; i <= last; i++) {
    if (buttons[i]!.type !== 'QUICK_REPLY')
      return 'Quick-reply buttons must be grouped together — place them all first or all last, not interleaved with other button types.';
  }
  return null;
}

function validateCarousel(dto: CreateTemplateInput): string | null {
  const carousel = dto.carousel;
  if (!carousel) return null;

  if (dto.category !== 'MARKETING' && dto.category !== 'UTILITY')
    return 'Carousel templates must be MARKETING or UTILITY.';

  // Carousels use per-card headers/buttons; the bubble has body text only.
  if (dto.header && dto.header.format !== 'NONE')
    return 'Carousel templates use per-card headers — remove the top-level header.';
  if (dto.footer?.text) return 'Carousel templates cannot have a footer.';
  if (dto.buttons && dto.buttons.length > 0)
    return 'Carousel templates use per-card buttons — remove the top-level buttons.';

  if ((dto.parameterFormat ?? 'POSITIONAL').toUpperCase() === 'NAMED')
    return 'Carousel cards support positional variables only — set parameter format to POSITIONAL.';

  if (carousel.cards.length < 2 || carousel.cards.length > 10)
    return 'A carousel needs between 2 and 10 cards.';

  // Meta requires every card to have an identical component structure.
  const ref = carousel.cards[0]!;
  const refButtonTypes = (ref.buttons ?? []).map((b) => b.type).join(',');
  for (let i = 1; i < carousel.cards.length; i++) {
    const card = carousel.cards[i]!;
    if (card.header.format !== ref.header.format)
      return `Card ${i + 1} must use the same header format as card 1 (Meta requires identical components on every card).`;
    if (!!card.body !== !!ref.body)
      return `Card ${i + 1} must ${ref.body ? 'have' : 'not have'} body text — all cards must match card 1 (Meta requires identical components on every card).`;
    if ((card.buttons ?? []).map((b) => b.type).join(',') !== refButtonTypes)
      return `Card ${i + 1} must have the same buttons (types and order) as card 1 (Meta requires identical components on every card).`;
  }

  for (let i = 0; i < carousel.cards.length; i++) {
    const err = validateCarouselCard(carousel.cards[i]!, i);
    if (err) return err;
  }
  return null;
}

function validateCarouselCard(card: CarouselCard, index: number): string | null {
  const label = `Card ${index + 1}`;
  if (!card.header.handleId)
    return `${label}: upload the ${card.header.format.toLowerCase()} for this card first.`;
  if (card.body) {
    if (card.body.text.length > 160) return `${label}: card body is too long (max 160 characters).`;
    const positional = collectMatches(card.body.text, POSITIONAL_VAR);
    const named = collectMatches(card.body.text, NAMED_VAR);
    if (named.size > 0)
      return `${label}: carousel card bodies support positional variables ({{1}}) only.`;
    if (positional.size > 0 && (card.body.examples?.length ?? 0) !== positional.size)
      return `${label}: card body has ${positional.size} variable(s) — provide an example value for each.`;
  }
  if (card.buttons && card.buttons.length > 2)
    return `${label}: a carousel card can have at most 2 buttons.`;
  for (const b of card.buttons ?? []) {
    const err = validateButton(b);
    if (err) return `${label}: ${err}`;
  }
  return null;
}

function validateLimitedTimeOffer(dto: CreateTemplateInput): string | null {
  const lto = dto.limitedTimeOffer;
  if (!lto) return null;

  if (dto.category !== 'MARKETING') return 'Limited-time offer templates must be MARKETING.';
  if (dto.footer?.text) return 'Limited-time offer templates cannot have a footer.';

  const hf = (dto.header?.format ?? 'NONE').toUpperCase();
  if (!['NONE', 'IMAGE', 'VIDEO'].includes(hf))
    return 'Limited-time offer templates support only image or video headers.';

  if (lto.text.length > 16) return 'Offer text is too long (max 16 characters).';
  if (!isPlainButtonText(lto.text))
    return 'Offer text must be plain text — no variables, emojis, newlines, or formatting.';

  const buttons = dto.buttons ?? [];
  // Meta's own documented example always includes at least one button — an
  // expiring offer with no copy-code/URL button gives the recipient no way
  // to act on it, and templates without one have been observed to fail
  // review with a generic "(#100) Invalid parameter".
  if (buttons.length === 0)
    return 'Limited-time offer templates need at least one button — copy-code and/or URL.';
  for (const b of buttons) {
    if (b.type !== 'COPY_CODE' && b.type !== 'URL')
      return 'Limited-time offer templates support only copy-code and URL buttons.';
  }
  const copyIdx = buttons.findIndex((b) => b.type === 'COPY_CODE');
  const urlIdx = buttons.findIndex((b) => b.type === 'URL');
  if (copyIdx !== -1 && urlIdx !== -1 && copyIdx > urlIdx)
    return 'Copy-code button must come before the URL button in a limited-time offer template.';

  return null;
}

function validateButton(b: TemplateButton): string | null {
  switch (b.type) {
    case 'QUICK_REPLY':
      if (!isPlainButtonText(b.text))
        return `Quick-reply button label '${b.text}' must be plain text (no variables, emojis, newlines, or formatting).`;
      if (b.text.length > 25) return 'Quick-reply label is too long (max 25 characters).';
      return null;

    case 'URL': {
      if (!isPlainButtonText(b.text)) return `URL button label '${b.text}' must be plain text.`;
      if (!b.url) return 'URL button needs a URL.';
      if (!b.url.toLowerCase().startsWith('https://')) return 'URL button must use https://.';
      const matches = b.url.match(POSITIONAL_VAR) ?? [];
      if (matches.length > 1)
        return 'URL button supports at most one variable, and it must be at the end of the URL.';
      if (matches.length === 1 && !b.urlExample)
        return 'URL button uses a variable — provide an example URL.';
      return null;
    }

    case 'PHONE_NUMBER':
      if (!isPlainButtonText(b.text)) return `Phone button label '${b.text}' must be plain text.`;
      if (!b.phone_number) return 'Phone-number button needs a number.';
      if (!b.phone_number.startsWith('+'))
        return 'Phone number must be in E.164 format (start with +).';
      return null;

    case 'COPY_CODE':
      if (!b.copyCodeExample)
        return 'Copy-code button needs an example code (used for review preview).';
      if (b.copyCodeExample.length > 20)
        return 'Copy-code example is too long (max 20 characters).';
      return null;

    case 'FLOW':
      if (!b.text) return 'Flow button needs a label.';
      if (!b.flowId) return 'Flow button needs a Flow id.';
      return null;

    case 'CATALOG':
    case 'MPM':
    case 'VOICE_CALL':
      if (!b.text && b.type !== 'MPM') return `${b.type} button needs a label.`;
      return null;

    default:
      return `Unknown button type '${(b as { type: string }).type}'.`;
  }
}

/**
 * Plain-text rule for button labels — no variables, newlines, emojis, or
 * markdown formatting. Same logic as .NET's
 * WhatsAppApiService.SanitiseButtonText / TemplateValidator.IsPlainButtonText.
 */
export function isPlainButtonText(input: string | null | undefined): boolean {
  if (!input || input.trim().length === 0) return true;
  const trimmed = input.trim();
  POSITIONAL_VAR.lastIndex = 0;
  NAMED_VAR.lastIndex = 0;
  if (POSITIONAL_VAR.test(trimmed) || NAMED_VAR.test(trimmed)) {
    POSITIONAL_VAR.lastIndex = 0;
    NAMED_VAR.lastIndex = 0;
    return false;
  }
  POSITIONAL_VAR.lastIndex = 0;
  NAMED_VAR.lastIndex = 0;
  if (/[\n\r\t]/.test(trimmed)) return false;
  if (/[*_~`]/.test(trimmed)) return false;
  for (const ch of trimmed) {
    const cp = ch.codePointAt(0) ?? 0;
    if (cp > 0xffff) return false;
    if (cp >= 0x2600 && cp <= 0x27bf) return false;
  }
  return true;
}

function countMatches(input: string, regex: RegExp): number {
  const re = new RegExp(regex.source, regex.flags);
  let count = 0;
  while (re.exec(input) !== null) count++;
  return count;
}

function collectMatches(input: string, regex: RegExp): Set<string> {
  const re = new RegExp(regex.source, regex.flags);
  const out = new Set<string>();
  let m: RegExpExecArray | null;
  while ((m = re.exec(input)) !== null) out.add(m[1]!);
  return out;
}
